import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SaveIcon from '@mui/icons-material/Save';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LoadingButton from '@mui/lab/LoadingButton';
import { InputAdornment } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { portfolioService } from '../../api';
import { DecimalNumberField } from '../../components/DecimalNumberField';
import { SectionCard, CARD_BORDER } from '../../components/Section';
import COLORS from '../../constants/colors';
import { usePermissions } from '../../hooks';
import { PortfolioMinimumConfig, PortfolioMinimumConfigValues } from '../../types/portfolio';
import { describeFractions } from '../../utils/portfolio';
import { PortfolioMinimumConfigHistory } from './PortfolioMinimumConfigHistory';
import * as permissions from '../../utils/permissions';

type Tier = 'basic' | 'premium';

type FormState = {
  basicSubscriptionFloor: string;
  premiumSubscriptionFloor: string;
};

const ERROR_MESSAGES: Record<string, string> = {
  SUBSCRIPTION_FLOOR_BELOW_CALCULATED_MINIMUM: "A floor can't be below what this portfolio's funds require.",
};

const sar = (value: number) =>
  `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR`;

const toText = (value: number | null) => (value === null || value === undefined ? '' : String(value));

/** '' means "no value"; anything else must be a finite number. */
const parseNumber = (value: string): number | null => {
  if (value.trim() === '') {
    return null;
  }

  const parsed = Number(value.replace(/,/g, ''));

  return Number.isFinite(parsed) ? parsed : NaN;
};

const toForm = (config: PortfolioMinimumConfigValues): FormState => ({
  basicSubscriptionFloor: toText(config.basicSubscriptionFloor),
  premiumSubscriptionFloor: toText(config.premiumSubscriptionFloor),
});

const tableSx = {
  '& .MuiTableCell-root': { fontFamily: 'inherit', fontSize: '0.8125rem' },
};

interface PortfolioMinimumConfigSectionProps {
  portfolioId: number;
  /** Changes whenever the portfolio is re-fetched (e.g. after its funds or weights are saved). */
  reloadKey?: string | number;
}

/**
 * MAD-5480: a portfolio's minimum subscription — the Basic and Premium floors, each fund's minimum and
 * whether it allows fractions, and the minimum the funds require (fractional funds count too).
 * Saved through its own endpoint, which only SUPER_ADMIN can call. Premium access, weight editing
 * and fund limits are plan rules (Settings › Premium), not set here.
 */
export function PortfolioMinimumConfigSection({ portfolioId, reloadKey }: PortfolioMinimumConfigSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const canEdit = usePermissions({ items: [permissions.Portfolio.minimumConfigUpdate] });
  const [data, setData] = useState<PortfolioMinimumConfig | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const response = await portfolioService.fetchMinimumConfig(portfolioId);

      setData(response.data);
      setForm(toForm(response.data.config));
    } catch (error: any) {
      setLoadError(error?.message || 'Failed to load the minimum investment settings');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const parsed = useMemo(() => {
    if (!form) {
      return null;
    }

    return {
      basicSubscriptionFloor: parseNumber(form.basicSubscriptionFloor),
      premiumSubscriptionFloor: parseNumber(form.premiumSubscriptionFloor),
    };
  }, [form]);

  const floorError = (tier: Tier): string | null => {
    const floor = parsed?.[`${tier}SubscriptionFloor`];

    if (floor === null || floor === undefined) {
      return null;
    }

    if (Number.isNaN(floor) || floor < 0) {
      return 'Enter a positive amount, or leave empty for no floor.';
    }

    // Like the backend, only a floor being changed is checked: a saved floor below what the funds
    // require is harmless (users invest from the higher of the two) and must not block other edits.
    const isChanged = floor !== data?.config[`${tier}SubscriptionFloor`];

    if (data?.floorCheckApplies && isChanged && floor > 0 && floor < (data.calculatedMinimum ?? 0)) {
      return `Below the ${sar(data.calculatedMinimum ?? 0)} this portfolio's funds require. Enter ${sar(
        data.calculatedMinimum ?? 0,
      )} or more, or leave empty for no floor.`;
    }

    return null;
  };

  const changes = useMemo((): Partial<PortfolioMinimumConfigValues> => {
    if (!data || !form || !parsed) {
      return {};
    }

    const next: PortfolioMinimumConfigValues = {
      basicSubscriptionFloor: parsed.basicSubscriptionFloor,
      premiumSubscriptionFloor: parsed.premiumSubscriptionFloor,
    };

    return Object.fromEntries(
      (Object.keys(next) as (keyof PortfolioMinimumConfigValues)[])
        .filter((key) => next[key] !== data.config[key])
        .map((key) => [key, next[key]]),
    );
  }, [data, form, parsed]);

  const hasErrors = ['basic', 'premium'].some((tier) => floorError(tier as Tier));
  const hasChanges = Object.keys(changes).length > 0;

  const onSave = async () => {
    if (!data || hasErrors || !hasChanges) {
      return;
    }

    setSaving(true);

    try {
      const response = await portfolioService.saveMinimumConfig({ id: portfolioId, ...changes });

      setData(response.data);
      setForm(toForm(response.data.config));
      setHistoryKey((key) => key + 1);
      enqueueSnackbar('Minimum investment settings saved', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(ERROR_MESSAGES[error?.message] || error?.message || 'Saving failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const update = (patch: Partial<FormState>) => setForm((current) => (current ? { ...current, ...patch } : current));

  const renderResult = (tier: Tier) => {
    if (!data || !form || !parsed) {
      return null;
    }

    const floor = parsed[`${tier}SubscriptionFloor`];
    const usableFloor = typeof floor === 'number' && floor > 0 ? floor : 0;

    if (floorError(tier)) {
      return { value: '—', caption: 'Fix the floor to see the result' };
    }

    if (data.isCustom) {
      return usableFloor
        ? { value: `${sar(usableFloor)} or more`, caption: "The higher of the floor and what the user's funds need" }
        : { value: 'Depends on the funds', caption: "What the user's selected funds need — no floor set" };
    }

    const base = Math.max(usableFloor, data.calculatedMinimum ?? 0);

    if (!base) {
      return { value: 'Any amount', caption: 'No fund has a minimum and no floor is set' };
    }

    // Whether users may change the weights is a plan rule, so this is the minimum at the default weights.
    return {
      value: sar(base),
      caption:
        usableFloor >= (data.calculatedMinimum ?? 0)
          ? 'The floor, at the default weights'
          : "What the portfolio's funds require at the default weights",
    };
  };

  const renderTierCard = (tier: Tier) => {
    if (!form) {
      return null;
    }

    const isPremium = tier === 'premium';
    const result = renderResult(tier);
    const floorHelper =
      floorError(tier) ||
      (data?.floorCheckApplies
        ? `Must be at least ${sar(data.calculatedMinimum ?? 0)}. Leave empty or 0 for no floor.`
        : 'Lowest amount a user can invest. Leave empty or 0 for no floor.');

    return (
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          borderRadius: '12px',
          border: `1px solid ${isPremium ? alpha(COLORS.ORANGE, 0.35) : CARD_BORDER}`,
          backgroundColor: isPremium ? alpha(COLORS.ORANGE, 0.03) : COLORS.WHITE,
          p: '20px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', mb: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isPremium ? alpha(COLORS.ORANGE, 0.12) : alpha(COLORS.PRIMARY_BLUE, 0.1),
                color: isPremium ? COLORS.ORANGE : COLORS.PRIMARY_BLUE,
              }}
            >
              {isPremium ? <WorkspacePremiumIcon fontSize="small" /> : <PersonOutlineIcon fontSize="small" />}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: COLORS.MAIN_DARK, lineHeight: '20px' }}>
                {isPremium ? 'Premium' : 'Basic'}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: COLORS.LIGHT_GRAY }}>
                {isPremium ? 'Users with an active Premium subscription' : 'Users on the free plan'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <DecimalNumberField
          margin="normal"
          fullWidth
          id={`${tier}SubscriptionFloor`}
          name={`${tier}SubscriptionFloor`}
          label="Minimum Subscription Floor"
          placeholder="No floor"
          disabled={!canEdit}
          value={form[`${tier}SubscriptionFloor`]}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            update({ [`${tier}SubscriptionFloor`]: event.target.value } as Partial<FormState>)
          }
          endAdornment={<InputAdornment position="end">SAR</InputAdornment>}
          error={!!floorError(tier)}
          helperText={floorHelper}
        />

        <Divider sx={{ my: '16px' }} />

        <Box sx={{ mt: 'auto' }}>
          <Typography
            sx={{ fontSize: '0.75rem', color: COLORS.LIGHT_GRAY, textTransform: 'uppercase', letterSpacing: '0.04em' }}
          >
            Users invest from
          </Typography>
          <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, color: COLORS.MAIN_DARK, mt: '2px' }}>
            {result?.value}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: COLORS.DARK_GREY }}>{result?.caption}</Typography>
        </Box>
      </Paper>
    );
  };

  const renderFunds = () => {
    if (!data) {
      return null;
    }

    if (data.isCustom) {
      return (
        <Alert
          icon={<InfoOutlinedIcon />}
          severity="info"
          sx={{ mb: '20px', borderRadius: '10px', fontFamily: 'inherit' }}
        >
          <b>Custom Build</b> — users pick their own funds and weights, so there are no default weights to check the
          floor against. The minimum is worked out for each user when they invest; the floor is the lowest amount anyone
          can invest.
        </Alert>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{ borderRadius: '12px', border: `1px solid ${CARD_BORDER}`, overflow: 'hidden', mb: '20px' }}
      >
        <Box
          sx={{
            px: '16px',
            py: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
            backgroundColor: COLORS.X_LIGHT_CYAN,
            borderBottom: `1px solid ${CARD_BORDER}`,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.MAIN_DARK }}>
              Minimum required by the funds
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: COLORS.DARK_GREY }}>
              {data.floorCheckApplies
                ? 'Each fund needs fund minimum ÷ weight, whether or not it allows fractions. The largest of these is the lowest floor you can set.'
                : 'No fund has a minimum, so the floor alone sets the minimum.'}
            </Typography>
          </Box>
          <Chip
            label={data.floorCheckApplies ? sar(data.calculatedMinimum ?? 0) : 'No minimum'}
            sx={{
              fontWeight: 700,
              fontSize: '0.9375rem',
              backgroundColor: data.floorCheckApplies ? COLORS.PRIMARY_BLUE : COLORS.SECONDARY_GREEN,
              color: COLORS.WHITE,
            }}
          />
        </Box>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: COLORS.LIGHT_GRAY, fontWeight: 600 }}>Fund</TableCell>
              <TableCell align="right" sx={{ color: COLORS.LIGHT_GRAY, fontWeight: 600 }}>
                Fund minimum
              </TableCell>
              <TableCell align="right" sx={{ color: COLORS.LIGHT_GRAY, fontWeight: 600 }}>
                Weight
              </TableCell>
              <TableCell align="center" sx={{ color: COLORS.LIGHT_GRAY, fontWeight: 600 }}>
                Allows fractions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.funds.map((fund) => {
              const fractional = fund.allowFractions === true;

              return (
                <TableRow
                  key={fund.indexFundId}
                  sx={{ backgroundColor: fund.setsMinimum ? alpha(COLORS.PRIMARY_BLUE, 0.05) : undefined }}
                >
                  <TableCell sx={{ fontWeight: fund.setsMinimum ? 600 : 400 }}>
                    {fund.nameEn}
                    {fund.setsMinimum && (
                      <Chip
                        label="Sets the minimum"
                        size="small"
                        sx={{
                          ml: '8px',
                          height: 20,
                          fontSize: '0.6875rem',
                          backgroundColor: alpha(COLORS.PRIMARY_BLUE, 0.12),
                          color: COLORS.DARK_BLUE,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{fund.minimumSubscription ? sar(fund.minimumSubscription) : '—'}</TableCell>
                  <TableCell align="right">{fund.weight}%</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={describeFractions(fund.allowFractions)}
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        backgroundColor: fractional ? alpha(COLORS.SECONDARY_GREEN, 0.15) : alpha(COLORS.ORANGE, 0.15),
                        color: fractional ? COLORS.SECONDARY_GREEN : COLORS.ORANGE,
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  return (
    <SectionCard
      title="Minimum Subscription"
      description="Minimum subscription for Basic and Premium users, and what this portfolio's funds require"
      collapsible
      action={
        data?.isCustom ? (
          <Chip label="Custom Build" variant="outlined" sx={{ fontWeight: 600, color: COLORS.DARK_GREY }} />
        ) : undefined
      }
    >
      {loadError && (
        <Alert severity="error" sx={{ mb: '16px', borderRadius: '10px' }}>
          {loadError}
        </Alert>
      )}
      {loading && !data ? (
        <Skeleton variant="rectangular" width="100%" height={240} />
      ) : (
        data &&
        form && (
          <>
            {renderFunds()}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {renderTierCard('basic')}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderTierCard('premium')}
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', mt: '16px' }}>
              {!canEdit && (
                <Typography sx={{ fontSize: '0.8125rem', color: COLORS.LIGHT_GRAY }}>
                  Only a Super Admin can change these settings.
                </Typography>
              )}
              <LoadingButton
                type="button"
                variant="contained"
                endIcon={<SaveIcon />}
                loading={saving}
                loadingPosition="end"
                disabled={!canEdit || !hasChanges || hasErrors}
                onClick={onSave}
              >
                Save minimum settings
              </LoadingButton>
            </Box>
            <Box sx={{ mt: '20px' }}>
              <PortfolioMinimumConfigHistory portfolioId={portfolioId} reloadKey={`${reloadKey ?? ''}-${historyKey}`} />
            </Box>
          </>
        )
      )}
    </SectionCard>
  );
}
